/**
 * Admin Page — single page with sidebar tabs for all admin sub-pages.
 * Replaces 10 separate nav items with one "Admin" entry.
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plug, ClipboardList, Calendar, Users, Building2, BookOpen, WandSparkles, FileCode2, FolderPlus, Settings } from 'lucide-react';

const ADMIN_SECTIONS = [
  { id: 'integrations', label: 'Integrations', icon: Plug },
  { id: 'users', label: 'Users & Roles', icon: Users },
  { id: 'boards', label: 'Boards & Policies', icon: ClipboardList },
  { id: 'workspaces', label: 'Workspaces', icon: Building2 },
  { id: 'appointment-types', label: 'Appointment Types', icon: Calendar },
  { id: 'audit', label: 'Audit Log', icon: BookOpen },
  { id: 'ai-training', label: 'AI Training & Tuning', icon: WandSparkles },
  { id: 'script-library', label: 'Script Library', icon: FileCode2 },
  { id: 'category-suggestions', label: 'Category Suggestions', icon: FolderPlus },
  { id: 'settings', label: 'Settings', icon: Settings },
] as const;

export function AdminPage({ children, activeSection }: { children?: React.ReactNode; activeSection?: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine active section from props or URL
  const current = activeSection ?? (() => {
    for (const s of ADMIN_SECTIONS) {
      if (location.pathname.includes('/admin/' + s.id)) return s.id;
    }
    return 'integrations';
  })();

  return (
    <div className="flex h-[calc(100vh-3.5rem)]">
      {/* Sidebar */}
      <aside className="w-56 shrink-0 border-r border-[hsl(var(--border))] flex flex-col">
        <div className="p-4 border-b border-[hsl(var(--border))]">
          <h1 className="text-lg font-bold text-[hsl(var(--content))]">Admin</h1>
          <p className="text-xs text-[hsl(var(--content-subtle))]">{ADMIN_SECTIONS.length} sections</p>
        </div>
        <nav className="flex-1 overflow-y-auto p-2 space-y-1">
          {ADMIN_SECTIONS.map((section) => {
            const Icon = section.icon;
            const isActive = current === section.id;
            return (
              <button
                key={section.id}
                onClick={() => navigate('/admin/' + section.id)}
                className={
                  "flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left " +
                  (isActive
                    ? "bg-brand-50 dark:bg-brand-950/30 text-brand-600 dark:text-brand-400"
                    : "text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))] hover:text-[hsl(var(--content))]")
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                {section.label}
              </button>
            );
          })}
        </nav>
      </aside>
      
      {/* Main content */}
      <div className="flex-1 overflow-y-auto">
        {children}
      </div>
    </div>
  );
}

